import React, { useState, useEffect } from "react";
import { updateProfile, generateOtp, verifymodalOtp } from "../services/apis";
import { useAuth } from "../context/AuthContext";
import Cross_Icon from "../assests/cross.svg";
import toast from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";
import { BIRTHDAY_MONTHS, BIRTHDAY_MONTH_DAYS } from "../lib/utils";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFatalError?: (message: string) => void;
  user: {
    phone: string;
    email: string;
    name: string;
    id: string;
    birthday?: { month: number; day: number } | null;
  };
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  onFatalError,
  user,
}) => {
  const { user: authUser, setUser } = useAuth();

  const initialBirthMonth = user.birthday?.month ? String(user.birthday.month) : "";
  const initialBirthDay = user.birthday?.day ? String(user.birthday.day) : "";
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    birthMonth: initialBirthMonth,
    birthDay: initialBirthDay,
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalerror, setmodalError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  // Password fields stay hidden until asked for, so browser autofill can't
  // make a profile-only edit look like a password change
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const togglePasswordFields = () => {
    setShowPasswordFields((open) => !open);
    setFormData((prev) => ({ ...prev, oldPassword: "", newPassword: "", confirmPassword: "" }));
    setValidationErrors((prev) => ({ ...prev, oldPassword: "", newPassword: "", confirmPassword: "" }));
  };

  // NEW: Send OTP Function
  const handleSendOtp = async () => {
    setIsOtpModalOpen(true); // open OTP modal

    try {
      const res = await generateOtp({ email: formData.email });

      if (res.data.success) {
      } else {
        setError("Unable to send verification code. Please try again.");
      }
    } catch (err: any) {
      setError("Unable to send verification code. Please try again.");
    }
  };
  const handleVerifyOtp = async (otp: string) => {
    try {
      const res = await verifymodalOtp({ email: formData.email, otp });

      if (res.data.success) {
        setSuccess("Email verified successfully!");
        setIsOtpModalOpen(false);
        setIsEmailVerified(true);
      } else {
        setmodalError("The verification code is invalid. Please try again.");
      }
    } catch (err: any) {
      setmodalError("Unable to verify the code. Please try again.");
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: user.name,
        email: user.email,
        birthMonth: initialBirthMonth,
        birthDay: initialBirthDay,
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setError("");
      setSuccess("");
      setValidationErrors({});
      setIsEmailVerified(true);
      setShowPasswordFields(false);
    }
  }, [isOpen, user.name, user.email, initialBirthMonth, initialBirthDay]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    setError("");
    setmodalError("");

    if (name === "email") {
      if (!value.trim()) {
        setValidationErrors((prev) => ({
          ...prev,
          email: "Email is required",
        }));
      } else if (!/^\S+@\S+\.\S+$/.test(value)) {
        setValidationErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email",
        }));
      } else {
        setValidationErrors((prev) => ({ ...prev, email: "" }));
      }
      setIsEmailVerified(value === user.email);
    } else if (name === "newPassword") {
      if (value && !/^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(value)) {
        setValidationErrors((prev) => ({
          ...prev,
          newPassword:
            "Password must be 8+ characters including uppercase, number, and special character",
        }));
      } else {
        setValidationErrors((prev) => ({ ...prev, newPassword: "" }));
      }

      if (updatedFormData.confirmPassword) {
        if (value !== updatedFormData.confirmPassword) {
          setValidationErrors((prev) => ({
            ...prev,
            confirmPassword: "Passwords do not match",
          }));
        } else {
          setValidationErrors((prev) => ({ ...prev, confirmPassword: "" }));
        }
      }
    } else if (name === "confirmPassword") {
      const newPassword = updatedFormData.newPassword;

      if (value && newPassword !== value) {
        setValidationErrors((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match",
        }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    } else {
      if (validationErrors[name]) {
        setValidationErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const handleBirthdayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Drop a day the new month doesn't have (e.g. 31 after picking April)
      if (name === "birthMonth" && next.birthDay && value) {
        if (Number(next.birthDay) > BIRTHDAY_MONTH_DAYS[Number(value) - 1]) next.birthDay = "";
      }
      return next;
    });
    setValidationErrors((prev) => ({ ...prev, birthday: "" }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.birthMonth !== !formData.birthDay) {
      newErrors.birthday = "Choose both the month and the day";
    }

    if (
      formData.newPassword &&
      formData.newPassword !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (
      (formData.oldPassword && !formData.newPassword) ||
      (!formData.oldPassword && formData.newPassword)
    ) {
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
      if (!formData.newPassword) {
        newErrors.newPassword = "New password is required";
      }
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setmodalError("");
    setSuccess("");

    try {
      const response = await updateProfile({
        userId: user.id,
        name: formData.name,
        newEmail: formData.email,
        oldPassword: showPasswordFields ? formData.oldPassword : undefined,
        newPassword: showPasswordFields ? formData.newPassword : undefined,
        // A saved birthday is locked, so it's only sent the first time
        birthday: user.birthday?.month
          ? undefined
          : formData.birthMonth && formData.birthDay
            ? { month: Number(formData.birthMonth), day: Number(formData.birthDay) }
            : null,
      });

      if (response.data.success !== false) {
        setSuccess("Profile updated successfully!");

        if (authUser) {
          setUser({
            ...authUser,
            name: formData.name,
            email: formData.email,
            birthday: user.birthday?.month
              ? user.birthday
              : formData.birthMonth && formData.birthDay
                ? { month: Number(formData.birthMonth), day: Number(formData.birthDay) }
                : null,
          });
        }

        setFormData((prev) => ({
          ...prev,
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));

        toast.success("Profile updated successfully");
        onClose();
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (err: any) {
      console.error("Profile update request failed:", err);
      setError("We could not update your profile at this time.");
      onFatalError?.(
        "We could not update your profile at this time. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const hasChanges =
    formData.name !== user.name ||
    formData.email !== user.email ||
    formData.birthMonth !== initialBirthMonth ||
    formData.birthDay !== initialBirthDay ||
    formData.oldPassword !== "" ||
    formData.newPassword !== "";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center  pt-4 sm:pt-0 z-50 p-2 sm:p-4 ">
      <div className="dm-edit-profile bg-white rounded-2xl w-full max-w-2xl mx-2 sm:mx-4 shadow-2xl max-h-[95vh] sm:max-h-[85vh] overflow-y-auto no-scrollbar">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 sm:px-8 py-4 sm:py-6  border-b sticky top-0 bg-white z-10">
          <h2 className="text-[18px] sm:text-[20px] font-bold text-black">
            Edit Profile
          </h2>
          <button
            onClick={onClose}
            className="text-black hover:text-black/70 transition-colors p-2"
          >
            <img src={Cross_Icon} alt="Close" className="w-4 h-4 dm-icon-invert" />
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mx-4 sm:mx-8 mt-4 p-3 sm:p-4 bg-green-50 border-[1px] border-green-200 rounded-lg">
            <p className="text-green-700 text-sm sm:text-base">{success}</p>
          </div>
        )}
        {error && (
          <div className="mx-4 sm:mx-8 mt-4 p-3 sm:p-4 bg-red-50 border-[1px] border-red-200 rounded-lg">
            <p className="text-red-700 text-sm sm:text-base">{error}</p>
          </div>
        )}

        {/* Single Form */}
        <form onSubmit={handleSubmit} className="px-4 sm:px-8 py-6 sm:py-8">
          <div className="space-y-5 sm:space-y-6">
            {/* Name Field */}
            <div>
              <label
              htmlFor="name"
              className="block text-[14px] sm:text-[15px] font-semibold text-black mb-2"
              >
              Name
              </label>
              <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your name"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] border-black/20 rounded-lg text-base sm:text-lg text-black placeholder:text-black/50"
              required
              />
            </div>

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-[14px] sm:text-[15px] font-semibold text-black mb-2"
              >
                Email Address
              </label>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-0">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] border-black/20 rounded-lg  sm:rounded-r-none text-base sm:text-lg text-black placeholder:text-black/50"
                  required
                />

                <button
                  type="button"
                  className={`dm-profile-verify px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-l-none whitespace-nowrap text-sm sm:text-base
                  ${
                    isEmailVerified
                      ? "dm-profile-verify-disabled bg-gray-200 text-black/60 cursor-not-allowed"
                      : "dm-profile-verify-active bg-main text-white"
                  }
                `}
                  onClick={handleSendOtp}
                  disabled={isEmailVerified} // unclickable if verified
                >
                  {isEmailVerified ? "Verified" : "Verify"}
                </button>
              </div>
            </div>

            {/* Birthday (month and day) */}
            <div>
              <label
                htmlFor="birthMonth"
                className="block text-[14px] sm:text-[15px] font-semibold text-black mb-2"
              >
                Birthday
              </label>
              {user.birthday?.month ? (
                <>
                  <p className="m-0 px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] border-black/10 rounded-lg text-base sm:text-lg text-black/70">
                    {BIRTHDAY_MONTHS[user.birthday.month - 1]} {user.birthday.day}
                  </p>
                  <p className="text-black/50 text-sm mt-1 mb-0">Your birthday can't be changed once it's saved.</p>
                </>
              ) : (
              <>
              <div className="flex gap-2">
                <select
                  id="birthMonth"
                  name="birthMonth"
                  value={formData.birthMonth}
                  onChange={handleBirthdayChange}
                  aria-label="Birthday month"
                  className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] border-black/20 rounded-lg text-base sm:text-lg text-black bg-transparent"
                >
                  <option value="">Month</option>
                  {BIRTHDAY_MONTHS.map((month, index) => (
                    <option key={month} value={String(index + 1)}>
                      {month}
                    </option>
                  ))}
                </select>
                <select
                  id="birthDay"
                  name="birthDay"
                  value={formData.birthDay}
                  onChange={handleBirthdayChange}
                  aria-label="Birthday day"
                  className="w-28 px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] border-black/20 rounded-lg text-base sm:text-lg text-black bg-transparent"
                >
                  <option value="">Day</option>
                  {Array.from(
                    { length: formData.birthMonth ? BIRTHDAY_MONTH_DAYS[Number(formData.birthMonth) - 1] : 31 },
                    (_, index) => (
                      <option key={index + 1} value={String(index + 1)}>
                        {index + 1}
                      </option>
                    ),
                  )}
                </select>
              </div>
              {validationErrors.birthday && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.birthday}</p>
              )}
              <p className="text-black/50 text-sm mt-1 mb-0">Check it carefully: it can't be changed after you save it.</p>
              </>
              )}
            </div>

            {/* Divider */}
            <div className="border-t-[1px]  border-black/20  pt-4 mb-0 mt-6 sm:mt-8 flex items-center justify-between gap-3">
              <h3 className="text-lg sm:text-xl font-bold text-black m-0">
                Change Password
              </h3>
              <button
                type="button"
                onClick={togglePasswordFields}
                className="px-4 py-2 rounded-lg border-[1px] border-black/20 text-sm sm:text-base font-medium text-black"
              >
                {showPasswordFields ? "Keep current password" : "Change"}
              </button>
            </div>

            {showPasswordFields && (
            <>
            {/* Old Password */}
            <div className="relative">
              <label
                htmlFor="oldPassword"
                className="block text-[14px] sm:text-[15px] font-semibold text-black mb-2"
              >
                Current Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="oldPassword"
                autoComplete="current-password"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleInputChange}
                placeholder="Enter current password"
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] rounded-lg text-base sm:text-lg ${
                  validationErrors.oldPassword
                    ? "border-red-500"
                    : " border-black/20 "
                }`}
              />
              <span
                  className="absolute right-3 top-11 cursor-pointer text-black/70"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>
              {validationErrors.oldPassword && (
                <p className="text-red-500 text-xs sm:text-sm mt-1">
                  {validationErrors.oldPassword}
                </p>
              )}
            </div>

            {/* New Password */}
            <div className="relative">
              <label
                htmlFor="newPassword"
                className="block text-[14px] sm:text-[15px] font-semibold text-black mb-2"
              >
                New Password
              </label>
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                autoComplete="new-password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] rounded-lg text-base sm:text-lg ${
                  validationErrors.newPassword
                    ? "border-red-500"
                    : " border-black/20"
                }`}
              />
              <span
                  className="absolute right-3 top-11 cursor-pointer text-black/70"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>

              {validationErrors.newPassword && (
                <p className="text-red-500 text-xs sm:text-sm mt-1">
                  {validationErrors.newPassword}
                </p>
              )}
              {(formData.oldPassword || formData.newPassword) &&
                !validationErrors.newPassword && (
                  <p className="text-xs sm:text-sm text-black/70 mt-1">
                    Both current and new password are required to change
                    password
                  </p>
                )}
            </div>

            {/* Confirm Password Field */}
            <div className="relative">
              <label
                htmlFor="confirmPassword"
                className="block text-[14px] sm:text-[15px] font-semibold text-black mb-2"
              >
                Confirm Password
              </label>
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                autoComplete="new-password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Re-enter new password"
                className={`w-full px-3  sm:px-4 py-2 sm:py-2.5 border-[1px] rounded-lg text-base sm:text-lg ${
                  validationErrors.confirmPassword
                    ? "border-red-500"
                    : "border-[1px] border-black/20"
                }`}
              />
              <span
                  className="absolute right-3 top-11 cursor-pointer text-black/70"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </span>
              {validationErrors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.confirmPassword}
                </p>
              )}
            </div>
            </>
            )}

            {/* Action Buttons */}
            <div className="flex-col sm:flex-row gap-3 flex sm:gap-4 pt-4 sm:pt-6 ">
              <button
                type="submit"
                disabled={
                  loading ||
                  !hasChanges ||
                  Object.keys(validationErrors).some(
                    (key) => validationErrors[key]
                  ) ||
                  !isEmailVerified
                }
                className="dm-profile-update flex-1 sm:flex-1 px-6 py-2.5 bg-main/90 text-white text-base sm:text-lg font-semibold rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? "Updating..." : "Update Profile"}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="dm-profile-cancel w-full sm:w-auto px-6 py-2.5 border-[1px] border-black/20 text-black/70 text-base sm:text-lg  font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
        <OtpModal
          isOpen={isOtpModalOpen}
          onClose={() => setIsOtpModalOpen(false)}
          email={formData.email}
          onVerify={handleVerifyOtp}
          error={modalerror}
        />
      </div>
    </div>
  );
};

export default EditProfileModal;
const OtpModal = ({
  isOpen,
  onClose,
  email,
  onVerify,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  onVerify: (otp: string) => void;
  error: string;
}) => {
  const [otp, setOtp] = useState("");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[999] p-4">
      <div className="dm-edit-profile bg-white p-4 sm:p-6 rounded-xl w-full max-w-md shadow-xl mx-4 dm-otp-modal">
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 dm-otp-title">Verify OTP</h2>
        {error && (
          <div className="mx-0 sm:mx-8 p-2 sm:p-1 bg-red-50 border text-center border-red-200 rounded-lg mb-3">
            <p className="text-red-700 mb-0 text-sm sm:text-base">{error}</p>
          </div>
        )}
        <p className="text-black/60 text-xs sm:text-sm  mb-3 dm-otp-subtitle">
          We sent a verification code to <strong className="break-words">{email}</strong>
        </p>

        <input
          type="text"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="Enter OTP"
          className="w-full px-3 sm:px-4 py-2 border rounded-lg mb-4 text-base"
        />

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            type="button"
            className="sm:flex-1 bg-main/90 text-white py-2 rounded-lg text-sm sm:text-base dm-otp-verify"
            onClick={() => onVerify(otp)}
          >
            Verify
          </button>

          <button
            className="sm:flex-1 border-[1px] border-black/20 text-black/70 py-2 rounded-lg text-sm sm:text-base dm-otp-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
